import React, { useState } from "react";
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Collapse,
} from "@mui/material";
import { SelectChangeEvent } from "@mui/material";
import { useQueryClient } from "@tanstack/react-query";

import AccountsList from "./components/AccountsList";
import { APIFunds } from "../../../../api";
import { ITradedAccount } from "../../../../api/funds/index.types";
import { getAccountsToShow } from "./utils";
import { EVALUATIONS_API_URLS } from "../../../../api/evaluations";
import { EEvaluationType } from "../../../../constants";
import { getDialogContentText } from "../../../FundedTraders/components/TradedAccountsInfoTableColumns";

interface IProps {
  title: string;
  listItems: ITradedAccount[];
  priorAccounts: ITradedAccount[];
  fetchTradedAccounts: () => Promise<void>;
  withCollapse?: boolean;
}
interface IConfirmValue {
  id: number;
  accounts_id: number;
  processing_status?: string;
  account_type?: EEvaluationType;
  invoice_status?: string;
  evaluation_type?: EEvaluationType;
}
const TradedAccountsList: React.FC<IProps> = ({
  title,
  listItems,
  fetchTradedAccounts,
  withCollapse,
  priorAccounts,
}) => {
  const queryClient = useQueryClient();

  const [isLoading, setIsLoading] = useState(false);

  const [isCollapsedPriorAccounts, setIsCollapsedPriorAccounts] = useState(false);
  const [isOpenDialogConfirm, setIsOpenDialogConfirm] = useState(false);
  const [dialogContent, setDialogContent] = useState({
    dialogTitle: "",
    dialogContent: "",
  });
  const [confirmValue, setConfirmValue] = useState<IConfirmValue>({
    id: -1,
    accounts_id: -1,
  });

  const handleSaveChangeProcessingStatus = async () => {
    try {
      setIsLoading(true);
      await APIFunds.updateAccountProcessingStatus(confirmValue);
      await fetchTradedAccounts();
    } catch (error) {
      console.error("Error update processing status", error);
    } finally {
      setIsLoading(false);
      setIsOpenDialogConfirm(false);
      await queryClient.invalidateQueries({
        queryKey: [EVALUATIONS_API_URLS.customerEvaluationCompleteHistory],
      });
    }
  };

  const handleChangeValue = (
    e: SelectChangeEvent<string>,
    id: number,
    accounts_id: number,
    e2t_account_id: string
  ) => {
    const name = e.target.name as string;
    const value = e.target.value as string;

    setDialogContent({
      dialogTitle: getDialogContentText(name, e2t_account_id, value).dialogTitle,
      dialogContent: getDialogContentText(name, e2t_account_id, value).dialogContentText,
    });

    if (e.target.value) {
      setConfirmValue({
        [name]: value,
        id,
        accounts_id,
      });
      setIsOpenDialogConfirm(true);
    }
  };

  const accountsToShow: ITradedAccount[] = getAccountsToShow(listItems);
  const accountsCount = accountsToShow.length;

  return (
    <div>
      <Dialog
        open={isOpenDialogConfirm}
        onClose={() => setIsOpenDialogConfirm(!isOpenDialogConfirm)}
      >
        <DialogTitle>{dialogContent.dialogTitle}</DialogTitle>
        <DialogContent>
          <DialogContentText>{dialogContent.dialogContent}</DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setIsOpenDialogConfirm(false);
            }}
            size="small"
            disabled={isLoading}
          >
            Cancel
          </Button>
          <Button
            onClick={handleSaveChangeProcessingStatus}
            size="small"
            color="secondary"
            autoFocus
            disabled={isLoading}
            data-test="button-submit"
          >
            Confirm
          </Button>
        </DialogActions>
      </Dialog>

      <b>{title}</b>
      {withCollapse && accountsCount ? (
        <div>
          <Collapse in={isCollapsedPriorAccounts} timeout="auto" unmountOnExit>
            <AccountsList
              listItems={listItems}
              accountsToShow={accountsToShow}
              priorAccounts={priorAccounts}
              handleChangeValue={handleChangeValue}
            />
          </Collapse>
          <Button
            sx={{ m: "26px 0 0 5px" }}
            onClick={() => setIsCollapsedPriorAccounts(!isCollapsedPriorAccounts)}
          >
            {isCollapsedPriorAccounts ? "Hide" : "Show"} `{accountsCount}` prior accounts
          </Button>
        </div>
      ) : (
        !!accountsCount && (
          <AccountsList
            listItems={listItems}
            accountsToShow={accountsToShow}
            priorAccounts={priorAccounts}
            handleChangeValue={handleChangeValue}
          />
        )
      )}
    </div>
  );
};

export default TradedAccountsList;
